import {
  QMS_ALL_PERMISSIONS,
  QMS_ROLE_PROFILE_PERMISSIONS,
} from "./qmsPermissions";
import {
  organizationHasCapaModule,
  organizationHasQmsModule,
  organizationHasIsfModule,
  organizationHasSponsorCroManagementFeature,
} from "../utils/orgModuleAccess";

/** Legacy/runtime keys — not assignable via role/user permission UI */
export const NON_ASSIGNABLE_PERMISSION_KEYS = ["AdminRole", "ADMIN_ROLE"];

export function filterAssignablePermissionKeys(keys = []) {
  const blocked = new Set(
    NON_ASSIGNABLE_PERMISSION_KEYS.map((key) => key.toLowerCase()),
  );
  return (Array.isArray(keys) ? keys : []).filter(
    (key) => !blocked.has(String(key).toLowerCase()),
  );
}

export const PERMISSIONS = {
  ADMIN: [
    { key: "USER_MANAGEMENT", label: "User Management" },
    { key: "SPONSOR_CRO_MANAGEMENT", label: "Sponsor/CRO management" },
    { key: "CHANGE_CREDENTIALS", label: "Change Credentials" },
    { key: "ROLE_MANAGEMENT", label: "Role Management" },
    { key: "SITE_MANAGEMENT", label: "Site Management" },
    { key: "DEPARTMENT_MANAGEMENT", label: "Departments" },
    { key: "CAPACITY_MANAGEMENT", label: "Capacity Management" },
    { key: "CAPA_CONFIGURE", label: "Can Configure CAPA" },
    { key: "QMS_CONFIGURE", label: "Can Configure QMS" },
    { key: "EISF_CONFIGURE", label: "Can Configure eISF" },
    { key: "ADMIN_DASHBOARD", label: "Dashboard" },
  ],
  STUDY_MANAGEMENT: [
    { key: "STUDY_LIST", label: "Study List" },
    { key: "ARCHIVED_STUDY_LIST", label: "Archived Study List" },
    { key: "ADD_STUDY", label: "Add Study" },
    { key: "EDIT_STUDY", label: "Edit Study" },
    { key: "ADD_EDIT_SCHEDULE", label: "Add Edit Schedule" },
    { key: "CHANGE_STATUS", label: "Change Status" },
  ],

  ISF_PSF: [
    { key: "ISF_DASHBOARD", label: "eISF/ePSF Dashboard" },
    { key: "ISF_REPORTS", label: "eISF/ePSF Report" },
    { key: "VIEW_ISF", label: "View eISF/ePSF" },
    { key: "CREATE_ISF", label: "Create eISF/ePSF" },
    { key: "CREATE_FOLDER", label: "Create Folder in eISF/ePSF" },
    { key: "CREATE_SUB_FOLDER", label: "Create Sub-Folder in eISF/ePSF" },
    { key: "UPDATE_FOLDER", label: "Rename Folder in eISF/ePSF" },
    { key: "MOVE_FOLDER", label: "Move Folder in eISF/ePSF" },
    { key: "REMOVE_FOLDER", label: "Remove Folder in eISF/ePSF" },
    { key: "GENERATE_CERTIFICATION", label: "Generate Certification" },
    { key: "CREATE_STUDY_ADHOC_PLACEHOLDER", label: "Add Study Ad-hoc Placeholders" },
    { key: "EDIT_STUDY_ADHOC_PLACEHOLDER", label: "Edit Study Ad-hoc Placeholders" },
    { key: "REMOVE_STUDY_ADHOC_PLACEHOLDER", label: "Remove Study Ad-hoc Placeholders" },
    { key: "FOLDER_LIBRARY", label: "Folder Library" },
    { key: "MILESTONE_LIBRARY", label: "Milestone Library" },
    { key: "MILESTONE_PLAN", label: "Milestone Plan" },
    { key: "TEMPLATE_MANAGER", label: "Template Manager" },
    { key: "AUDIT_LOG", label: "Audit Log" },
  ],
  CAPA: [
    { key: "VIEW_CAPA", label: "Can View CAPA List" },
    { key: "ADD_CAPA", label: "Can Create CAPA" },
    { key: "VIEW_REMOVED_CAPA", label: "Can View Removed CAPA List" },
    { key: "CAPA_REPORTS", label: "Can View CAPA Reports" },
    { key: "CAPA_AUDIT_LOG", label: "Can View CAPA Audit Log" },
  ],

  EPLANNER: [
    { key: "BOOK_APPOINTMENT", label: "Book Appointment" },
    { key: "CANCEL_APPOINTMENT", label: "Cancel Appointment" },
    { key: "RESCHEDULE_APPOINTMENT", label: "Reschedule Appointments" },
    { key: "CHANGE_APPOINTMENT_STATUS", label: "Appointment Status Change" },
    { key: "VIEW_APPOINTMENT_LIST", label: "View Appointment List" },
    { key: "VIEW_APPOINTMENT_CALENDAR", label: "Appointment Calendar" },
    { key: "VIEW_LOCATION_OCCUPANCY", label: "Location Occupancy" },
    { key: "VIEW_APPOINTMENT_CHANGES", label: "Appointment Changes" },
    { key: "VIEW_EPLANNER_ANALYTICS", label: "Analytics" },
    { key: "VIEW_WHATSAPP_COMMUNICATION", label: "Communication" },
  ],

  PARTICIPANT_MANAGER: [
    { key: "PARTICIPANT_MANAGER", label: "Participant Manager" },
    { key: "CAMPAIGN_TEMPLATES", label: "Campaign Templates" },
    { key: "FEASIBILITY_MANAGER", label: "Feasibility Manager" },
    { key: "SEND_BULK_EMAIL_SMS", label: "Send Bulk Email/SMS" },
  ],

  /** Full QMS registry (runtime). Role/profile card uses QMS_ROLE_PROFILE_PERMISSIONS only. */
  QMS: QMS_ALL_PERMISSIONS,
  QMS_ROLE_PROFILE: QMS_ROLE_PROFILE_PERMISSIONS,
};

/** Admin card options — hide configure keys when org does not licence that product. */
export function getAdminPermissionsForOrg(userData) {
  const hasCapa = organizationHasCapaModule(userData);
  const hasQms = organizationHasQmsModule(userData);
  const hasIsf = organizationHasIsfModule(userData);

  return PERMISSIONS.ADMIN.filter((p) => {
    if (p.key === "ADMIN_DASHBOARD") return false;
    if (NON_ASSIGNABLE_PERMISSION_KEYS.includes(p.key)) return false;
    if (p.key === "CAPA_CONFIGURE") return hasCapa;
    if (p.key === "QMS_CONFIGURE") return hasQms;
    if (p.key === "EISF_CONFIGURE") return hasIsf;
    if (p.key === "SPONSOR_CRO_MANAGEMENT") {
      return organizationHasSponsorCroManagementFeature(userData);
    }
    return true;
  });
}
